import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  ConfigProvider,
  Flex,
  Select,
  Switch,
  Table,
  TableProps,
  Typography,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import PageHeading from "components/PageHeading.component";
import PATHS from "shared/constants/paths";
import { translate, translateWithVars } from "shared/languages";
import { getFrequencyLabels } from "pages/payroll/FrequencyForm";
import WarningBox, { WarningLink } from "components/WarningBox";
import { localDateOnly } from "shared/utils/dayjs";
import { th } from "shared/components/TableText";
import { colors } from "shared/theme/color";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { PayrollEligibleClosingDate, PayrollStaffOption } from "shared/types";
import { PayrollRunStatus, UserType } from "shared/types/enum";
import { ResponseHandler } from "shared/types/axios";
import {
  payrollRunsOptions,
  payrollSettingsOptions,
  payrollStaffOptions,
} from "hooks/queries/payroll";
import { useGeneratePayrollRun } from "hooks/mutations/payroll";
import { getApiErrorMessage, USER_ROLE_LABELS } from "shared/utils";
import PayrollSettingsRequired from "./PayrollSettingsRequired";
import { textPx } from "shared/constants/zoom";

// The server refuses generateRun with translate('payrollPage.errors.<key>')
// markers (src/services/payroll/shared.ts). Three of them get a richer Alert
// with a link to Riwayat instead of a bare toast; anything else falls back to
// the standard getApiErrorMessage toast, which resolves the marker itself.
// Matched on the key, so the wording can change in either language without
// touching this.
// Every row prints the cadence that produced it under its period, with or
// without a frequency change in flight (chris, 2026-10-14: always show it).
// A row's state in one word, or nothing when the period is ordinary. It sits
// on the right of the period rather than after a dot, so the cadence beneath
// and the state beside it never read as one run-on line.
const optionState = (date: PayrollEligibleClosingDate): string => {
  if (date.unavailable) {
    return translate(
      date.unavailable_reason === "not_ended"
        ? "payrollPage.run_start.period_not_ended_short"
        : "payrollPage.run_start.period_unavailable"
    );
  }
  if (date.clipped)
    return translate("payrollPage.run_start.period_clipped_short");
  return "";
};

// The sentence under the field once a date is picked. Days left unpaid
// earlier in the period are the most important thing to say, so they win.
const periodNote = (date: PayrollEligibleClosingDate): string => {
  if (date.leftover_days && date.leftover_label) {
    return translateWithVars("payrollPage.run_start.period_leftover", {
      days: String(date.leftover_days),
      label: date.leftover_label,
    });
  }
  if (date.clip_reason === "frequency_change")
    return translate("payrollPage.run_start.period_clipped_frequency");
  // Cut by an earlier payroll: name the days it already covers ("14 Sep"), the
  // server-formatted label an older server does not send, so a bare fallback
  // stays for that case.
  if (date.clipped) {
    return date.paid_label
      ? translateWithVars("payrollPage.run_start.period_clipped_paid", {
          label: date.paid_label,
        })
      : translate("payrollPage.run_start.period_clipped");
  }
  return "";
};

const CONFLICT_KEYS = {
  in_progress: "payrollPage.errors.run_in_progress",
  exists_for_period: "payrollPage.errors.run_exists_for_period",
} as const;
type ConflictAlert = keyof typeof CONFLICT_KEYS;
// The same two sentences as the toast strings above, split into the fact
// and the action so the box can weight them; the toast keeps the whole
// sentence, since a toast cannot carry bold.
const CONFLICT_TEXT: Record<ConflictAlert, { fact: string; action: string }> = {
  in_progress: {
    fact: "payrollPage.run_start.open_run_fact",
    action: "payrollPage.run_start.open_run_action",
  },
  exists_for_period: {
    fact: "payrollPage.run_start.run_exists_fact",
    action: "payrollPage.run_start.run_exists_action",
  },
};

// Employee type first, then the single highest admin tier held, if any. A
// role the person no longer holds for payroll (a deactivated account, or an
// admin whose instructor role was removed) comes back with active: false and
// renders grey, so the rows listed only under "Sertakan yang tidak aktif"
// read apart from the active ones at a glance.
const displayRoles = (record: PayrollStaffOption): { role: UserType; active: boolean }[] => {
  const roles = record.user.roles;
  const deactivated = !!record.deleted_at;
  const out: { role: UserType; active: boolean }[] = [];
  if (roles.includes(UserType.Instructor)) out.push({ role: UserType.Instructor, active: !deactivated });
  else if (roles.includes(UserType.Staff)) out.push({ role: UserType.Staff, active: !deactivated });
  else if (record.former_instructor) out.push({ role: UserType.Instructor, active: false });
  if (roles.includes(UserType.Manager)) out.push({ role: UserType.Manager, active: !deactivated });
  else if (roles.includes(UserType.Admin)) out.push({ role: UserType.Admin, active: !deactivated });
  return out;
};

const RunPayrollStartPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: settings } = useQuery(payrollSettingsOptions());
  const [closingDate, setClosingDate] = useState<string | undefined>();
  const [includeDeparted, setIncludeDeparted] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [conflictAlert, setConflictAlert] = useState<ConflictAlert | null>(
    null
  );
  const { data: staff = [], isLoading: staffLoading } = useQuery(
    payrollStaffOptions(includeDeparted)
  );
  const { mutate: generateRun, isPending } = useGeneratePayrollRun();
  // Only one payroll can be open at a time (the server enforces it). Said
  // here, on arrival, instead of letting someone pick a period and choose
  // staff only to be refused on the last click.
  const { data: runs } = useQuery(payrollRunsOptions());
  const openRun = runs?.find(
    (r) =>
      r.status === PayrollRunStatus.Draft ||
      r.status === PayrollRunStatus.Reopened
  );
  const selectedDate = settings?.eligibleClosingDates.find(
    (d) => d.closingDate === closingDate
  );
  // A date can leave the list under the admin's feet: another manager runs a
  // payroll, or a frequency change cuts this week short, and the page
  // refetches on focus. Without this the Select would keep showing the
  // bare "2026-10-28" it can no longer resolve, and Generate would fail on a
  // period that is no longer offered.
  useEffect(() => {
    if (closingDate && settings?.eligibleClosingDates && !selectedDate)
      setClosingDate(undefined);
  }, [closingDate, selectedDate, settings?.eligibleClosingDates]);
  // A refused generate refetches the runs; once the run that blocks is known
  // the refusal banner gives way to the open-run banner with its link. The
  // picker is locked while a run is open, so nothing else could clear it.
  const openRunId = openRun?.id;
  useEffect(() => {
    if (openRunId) setConflictAlert(null);
  }, [openRunId]);
  // One line about a frequency change, so the two cadences in the list below
  // are explained: what the older periods still follow, or what is coming.
  const previousCadence = settings?.exists ? settings.previous_change : null;
  const pendingCadence = settings?.exists ? settings.pending_change : null;
  // `until` and `effective_from` are bare Jakarta calendar days, so they go
  // through localDateOnly, not localTzDate (see shared/utils/dayjs).
  const cadenceNote = previousCadence
    ? translateWithVars("payrollPage.settings.previous_note", {
        frequency: getFrequencyLabels()[previousCadence.frequency],
        date: localDateOnly(previousCadence.until).format("D MMM YYYY"),
      })
    : pendingCadence
    ? translateWithVars("payrollPage.settings.pending_note", {
        frequency: getFrequencyLabels()[pendingCadence.frequency],
        date: localDateOnly(pendingCadence.effective_from).format("D MMM YYYY"),
      })
    : null;

  // Muted breadcrumb back-link, same pattern as ClassCommissionPage.tsx /
  // PayrollHistoryPage.tsx. This page has two parents: Penggajian (its header
  // button and the setup guide's last step) and Riwayat (its own button), and
  // each passes itself as `state.from`. The link names that parent and
  // returns there the way the browser's Back does, so it never lands on the
  // other one and never adds a history entry. Opened with no `from` (a typed
  // URL), it falls back to Riwayat as before.
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const backTo = from === PATHS.PAYROLL ? PATHS.PAYROLL : PATHS.PAYROLL_HISTORY;
  // react-router numbers its entries in history.state.idx (0 = the page this
  // tab first loaded), so idx > 0 with a `from` means that parent is the
  // entry right behind this one.
  const goBack = () =>
    from && (window.history.state?.idx ?? 0) > 0
      ? navigate(-1)
      : navigate(backTo);
  const backButton = (
    <Button
      type="text"
      icon={<ArrowLeftOutlined />}
      onClick={goBack}
      style={{ paddingLeft: 0, color: colors.ink2, fontWeight: 400 }}
    >
      {backTo === PATHS.PAYROLL
        ? translate("payrollPage.rates.back_to_settings")
        : translate("payrollPage.run.back_to_history")}
    </Button>
  );

  const handleGenerate = () => {
    if (!closingDate || selectedRowKeys.length === 0) return;
    setConflictAlert(null);
    generateRun(
      { closing_date: closingDate, staff_ids: selectedRowKeys },
      {
        // Replace, not push: Back from the new draft then returns to the page
        // the start page was opened from, not to this form, whose only
        // remaining outcome would be the run_in_progress refusal.
        onSuccess: (run) =>
          navigate(`${PATHS.PAYROLL_RUN}/${run.id}`, { replace: true }),
        onError: (error: AxiosError<ResponseHandler>) => {
          const serverMessage = error.response?.data?.message;
          const conflict = (Object.keys(CONFLICT_KEYS) as ConflictAlert[]).find(
            (key) =>
              typeof serverMessage === "string" &&
              serverMessage.includes(CONFLICT_KEYS[key])
          );
          if (conflict) {
            setConflictAlert(conflict);
            // The refusal means the studio's runs or periods changed under
            // this page (another manager opened a run, or ran this period).
            // Refresh both reads now, so once the banner is cleared the page
            // already shows the run that blocks, with its link, and the
            // picker no longer offers the period that was just refused.
            queryClient.invalidateQueries({ queryKey: ["payroll", "runs"] });
            queryClient.invalidateQueries({
              queryKey: ["payroll", "settings"],
            });
          } else {
            getApiErrorMessage(error);
          }
        },
      }
    );
  };

  const columns: TableProps<PayrollStaffOption>["columns"] = [
    {
      title: th(translate("payrollPage.run_start.staff_col_name")),
      dataIndex: ["user", "full_name"],
      render: (_, record) => (
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Text style={{ fontWeight: 600 }}>
            {record.user.full_name}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      // Same StatusBadge/tone="navy"/`user_type.${role}` convention as the
      // "Peran" badge on the Staff Info page (UserProfile.component.tsx),
      // rather than plain joined text, so a role reads the same everywhere
      // in the app. Wrapped in a Flex since a person can hold multiple
      // roles (e.g. a manager who also teaches).
      title: th(translate("payrollPage.run_start.staff_col_role")),
      dataIndex: ["user", "roles"],
      // Collapsed the way every other page shows roles: the employee type
      // (Instruktur or Staf) plus, if held, ONE admin tier. A Manager's roles
      // always include "admin" too (Manager is additive on the server), so
      // printing the raw array showed "Admin, Manager, Instruktur" for a
      // manager who teaches.
      render: (_: string[], record: PayrollStaffOption) => (
        <Flex gap={6} wrap>
          {displayRoles(record).map(({ role, active }) => (
            // Role chips are labels (no dot): navy for a role held now, grey
            // for one the person no longer holds (deactivated, or an admin
            // whose instructor role was removed).
            <StatusBadge
              key={role}
              tone={active ? "navy" : "neutral"}
              label={USER_ROLE_LABELS[role]}
              dot={false}
            />
          ))}
        </Flex>
      ),
    },
  ];

  if (settings && !settings.exists) {
    return (
      <PayrollSettingsRequired
        heading={backButton}
        title={translate("payrollPage.run_start.title")}
        description={translate("payrollPage.run_start.empty_settings_desc")}
      />
    );
  }

  return (
    <>
      <PageHeading title={backButton} />
      <Typography.Title level={3} style={{ margin: "0 0 20px" }}>
        {translate("payrollPage.run_start.title")}
      </Typography.Title>
      <Flex vertical gap={16}>
        {openRun && !conflictAlert && (
          <WarningBox
            items={[
              {
                fact: translate(CONFLICT_TEXT.in_progress.fact),
                action: translate(CONFLICT_TEXT.in_progress.action),
              },
            ]}
            extra={
              <WarningLink
                onClick={() => navigate(`${PATHS.PAYROLL_RUN}/${openRun.id}`)}
              >
                {translate("payrollPage.errors.view_open_run")}
              </WarningLink>
            }
          />
        )}
        {conflictAlert && (
          // Cleared when another period is picked (see the Select), since the
          // refusal was about the one just tried.
          <WarningBox
            items={[
              {
                fact: translate(CONFLICT_TEXT[conflictAlert].fact),
                action: translate(CONFLICT_TEXT[conflictAlert].action),
              },
            ]}
            extra={
              <WarningLink onClick={() => navigate(PATHS.PAYROLL_HISTORY)}>
                {translate("payrollPage.errors.view_history")}
              </WarningLink>
            }
          />
        )}

        {/* While a run is open nothing on this page can be submitted, so the
            period select, the roster switch and the checkboxes are visible
            but locked (the house pattern the settings page uses before its
            first save), with the banner above saying why and linking to the
            open run. `|| undefined`, not a boolean: AntD only installs a
            DisabledContext when the prop is defined. */}
        <ConfigProvider componentDisabled={!!openRun || undefined}>
          <Card styles={{ body: { padding: 18 } }}>
            {/* During a frequency change the list mixes both cadences, so say
              which one the older periods still follow. Without it a studio
              that has just switched to Monthly sees only weekly dates (its
              unpaid weeks) and reasonably wonders why. */}
            {cadenceNote && (
              <Typography.Text
                type="secondary"
                style={{
                  display: "block",
                  marginBottom: 14,
                  fontSize: textPx(13),
                }}
              >
                {cadenceNote}
              </Typography.Text>
            )}
            <Flex vertical gap={12} style={{ maxWidth: 360 }}>
              <Typography.Text strong>
                {translate("payrollPage.run_start.closing_date_label")}
              </Typography.Text>
              <Select
                placeholder={translate(
                  "payrollPage.run_start.closing_date_placeholder"
                )}
                value={closingDate}
                onChange={(value) => {
                  setClosingDate(value);
                  setConflictAlert(null);
                }}
                // A date whose period is already covered by a run (any status)
                // is offered greyed out rather than refused after the click.
                // The server resolves every date against the studio's runs and
                // sends the CLIPPED period, the one stretch no run has paid,
                // trimmed at its start, its end, or both; periodLabel shows
                // it. A period left in two unpaid pieces is NOT refused: the
                // last piece is offered and the earlier days are named in the
                // sentence under the field, then offered on their own once
                // this one has been run.
                // The period IS the choice, so it is the row's headline; the
                // closing date it is keyed on repeats its last day and was only
                // noise. Everything else about the row (which cadence produced
                // it, whether it was shortened or is already paid) sits on one
                // quiet second line, so a row is one line when there is nothing
                // to say and two when there is.
                options={settings?.eligibleClosingDates.map((d) => ({
                  value: d.closingDate,
                  label: d.periodLabel,
                  disabled: !!d.unavailable,
                }))}
                // Two columns rather than a run-on line: the period and its
                // state read across the top, the cadence sits quietly under
                // the period. Every row is the same two lines, so the eye can
                // run down the left for dates and the right for states.
                optionRender={(option) => {
                  const match = settings?.eligibleClosingDates.find(
                    (d) => d.closingDate === option.value
                  );
                  const state = match ? optionState(match) : "";
                  // A row that cannot be run is muted on every line, keeping the
                  // same internal order. Every colour is explicit because AntD's
                  // Typography sets its own and would otherwise ignore both the
                  // inherited disabled grey and the hierarchy below.
                  const tone = match?.unavailable
                    ? {
                        period: colors.ink3,
                        state: colors.ink3,
                        cadence: colors.ink4,
                      }
                    : {
                        period: colors.ink,
                        state: colors.ink2,
                        cadence: colors.ink3,
                      };
                  return (
                    // Three levels so a row is read in order rather than all at
                    // once: the period anchors it, its state answers "can I use
                    // this", and the cadence sits furthest back.
                    <Flex vertical gap={3} style={{ padding: "3px 0" }}>
                      <Flex justify="space-between" align="baseline" gap={10}>
                        <span style={{ fontWeight: 500, color: tone.period }}>
                          {option.label}
                        </span>
                        {state && (
                          <Typography.Text
                            style={{
                              fontSize: textPx(12),
                              whiteSpace: "nowrap",
                              color: tone.state,
                            }}
                          >
                            {state}
                          </Typography.Text>
                        )}
                      </Flex>
                      {match?.frequency && (
                        <Typography.Text
                          style={{
                            fontSize: textPx(12),
                            color: tone.cadence,
                            lineHeight: 1.3,
                          }}
                        >
                          {getFrequencyLabels()[match.frequency]}
                        </Typography.Text>
                      )}
                    </Flex>
                  );
                }}
              />
              {selectedDate &&
                !selectedDate.unavailable &&
                periodNote(selectedDate) && (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: textPx(13) }}
                  >
                    {periodNote(selectedDate)}
                  </Typography.Text>
                )}
            </Flex>
          </Card>

          {/* `recently_closed` is also true for a period that has not ended at
            all (its end is in the future), and a selection made before a
            refetch can turn unavailable under the admin's feet, so the
            warning is tied to a date that can actually be run. */}
          {selectedDate?.recently_closed && !selectedDate.unavailable && (
            // Soft warning, never a block: the period's last classes may not
            // all be marked yet (server period.ts
            // isPeriodRecentlyClosed). The draft's own unmarked-session note
            // then says which sessions, and Hitung Ulang picks them up.
            <WarningBox
              items={[
                {
                  fact: translate(
                    "payrollPage.run_start.recently_closed_title"
                  ),
                  action: translate(
                    "payrollPage.run_start.recently_closed_desc"
                  ),
                },
              ]}
            />
          )}

          <Card styles={{ body: { padding: 18 } }}>
            <Flex justify="flex-end" style={{ marginBottom: 12 }}>
              <Flex align="center" gap={8}>
                <Switch
                  size="small"
                  checked={includeDeparted}
                  onChange={setIncludeDeparted}
                />
                <Typography.Text style={{ fontSize: textPx(13) }}>
                  {translate("payrollPage.run_start.include_departed")}
                </Typography.Text>
              </Flex>
            </Flex>
            <Table
              rowKey="user_id"
              size="middle"
              loading={staffLoading}
              columns={columns}
              dataSource={staff}
              rowSelection={{
                type: "checkbox",
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys.map(String)),
                // The row checkboxes read the DisabledContext too, but say it
                // here as well so the header's select-all follows for certain.
                getCheckboxProps: () => ({ disabled: !!openRun }),
              }}
              pagination={false}
            />
          </Card>
        </ConfigProvider>

        <Flex justify="flex-end">
          <ConfigProvider theme={modalButtonsTheme}>
            <Button
              type="primary"
              loading={isPending}
              // Also off when the picked period turned unavailable under the
              // admin's feet (a refetch after a refusal marks it processed):
              // the row stays listed, greyed, so the reset above keeps it.
              disabled={
                !!openRun ||
                !closingDate ||
                !!selectedDate?.unavailable ||
                selectedRowKeys.length === 0
              }
              onClick={handleGenerate}
            >
              {translate("payrollPage.run_start.generate_button")}
            </Button>
          </ConfigProvider>
        </Flex>
      </Flex>
    </>
  );
};

export default RunPayrollStartPage;
